/**
 * SealedChatScreen — una conversazione ANONIMA (sealed-sender, v2.2).
 *
 * Canale separato dalle chat normali: i messaggi viaggiano per "cassetta postale"
 * → il server NON sa chi-con-chi. Il testo decifrato vive solo qui (forward-secret:
 * la busta sul server si apre una volta sola). Poll periodico per ricevere.
 *
 * Effimero PARI ai messaggi normali: il mittente sceglie la durata (TTL), la
 * scadenza parte dalla PRIMA lettura, countdown live, e i messaggi scaduti spariscono.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { useStore } from '../lib/store';
import { sendSealed, receiveSealed } from '../lib/sealed';
import { getSealedConvo, deleteSealedConvo, markSealedViewed, isExpiredSealed } from '../lib/sealedStore';

const TTL_OPTIONS = [
  { value: 0, label: 'zero' },
  { value: 10, label: '10s' },
  { value: 300, label: '5m' },
  { value: 3600, label: '1h' },
  { value: 86400, label: '24h' },
  { value: -1, label: '∞' },
];

function fmtRemaining(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.ceil(s / 60)}m`;
  if (s < 86400) return `${Math.ceil(s / 3600)}h`;
  return `${Math.ceil(s / 86400)}g`;
}

export default function SealedChatScreen() {
  const navigate = useNavigate();
  const { peer } = useParams();
  const { boxPub, boxSec, sigil } = useStore();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ttl, setTtl] = useState(10);          // durata scelta dal mittente (default 10s)
  const [showTtl, setShowTtl] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());

  // Tick 1s per il countdown live di scomparsa.
  useEffect(() => {
    const i = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const refresh = useCallback(async () => {
    try { await receiveSealed({ boxKeypair: { pub: boxPub, priv: boxSec } }); } catch { /* poll */ }
    const c = await getSealedConvo(peer);
    setMsgs(c.messages);
  }, [peer, boxPub, boxSec]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  // Ancora la scadenza alla PRIMA lettura: appena i messaggi ricevuti sono a video,
  // segnali "letti" (fa partire il loro countdown), come nelle chat normali.
  useEffect(() => {
    const toView = msgs.filter((m) => !m.mine && m.viewedAt == null).map((m) => m.id);
    if (toView.length) { markSealedViewed(peer, toView).then((ch) => { if (ch) refresh(); }).catch(() => {}); }
  }, [msgs, peer, refresh]);

  // Messaggi visibili ora: scaduti nascosti in tempo reale (poi getSealedConvo li cancella).
  const visible = useMemo(() => msgs.filter((m) => !isExpiredSealed(m, nowTs)), [msgs, nowTs]);

  function remainingLabel(m) {
    if (m.stt == null || m.stt < 0) return null;         // legacy/∞ → nessun countdown
    if (m.stt === 0) return 'zero';
    const anchor = m.viewedAt;
    if (!anchor) return `${fmtRemaining(m.stt * 1000)} dopo la lettura`;
    return fmtRemaining(anchor + m.stt * 1000 - nowTs);
  }

  async function doSend() {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true); setErr(null);
    try {
      await sendSealed({ boxKeypair: { pub: boxPub, priv: boxSec }, peerSigil: peer, mySigil: sigil, text: t, ttl });
      setText('');
      const c = await getSealedConvo(peer); setMsgs(c.messages);
    } catch (e) {
      setErr(e?.message === 'PEER_PREKEYS_MISSING'
        ? `@${peer} non è ancora raggiungibile in anonimo. Deve aprire Nullum almeno una volta, con la versione più recente.`
        : e?.message === 'RECIPIENT_NOT_FOUND' ? 'Sigil non trovato.'
        : (e?.message || 'errore invio'));
    } finally { setBusy(false); }
  }

  async function doDelete() {
    if (!confirm('Eliminare questa chat anonima da questo dispositivo?')) return;
    await deleteSealedConvo(peer);
    navigate('/sealed', { replace: true });
  }

  const ttlLabel = TTL_OPTIONS.find((o) => o.value === ttl)?.label || `${ttl}s`;

  return (
    <Frame statusLeft="ANONIMO :: SEALED" statusRight="🧅 5G">
      <Header title={`// @${peer}`} crumb="◀ anonimo" onCrumbClick={() => navigate('/sealed')} />

      <div className="flex items-center gap-2 text-[12px] text-amber border border-amber/40 rounded-lg px-2.5 py-1.5 mb-3">
        <span>🛡</span>
        <span className="text-ink-dim">Il server <b className="text-amber">non sa</b> che vi state scrivendo.</span>
      </div>

      {visible.length > 0 && (
        <button onClick={doDelete} className="text-[12px] text-ink-dim hover:text-red-500 mb-2 self-end ml-auto block">
          🗑 elimina (solo qui)
        </button>
      )}

      <div className="flex-1 space-y-2 mb-3 min-h-[40vh]">
        {visible.length === 0 && (
          <p className="text-[13px] text-ink-dim text-center py-6">Ancora niente. Scrivi tu per primo, in anonimo.</p>
        )}
        {visible.map((m) => {
          const rem = remainingLabel(m);
          return (
            <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-xl px-3 py-2 text-[15px] border ${m.mine ? 'border-ink/40 bg-ink/[0.06] text-ink' : 'border-amber/40 bg-amber/[0.05] text-ink'}`}>
                <div>{m.text}</div>
                {rem && <div className="text-[10px] text-ink-dim mt-1 flex items-center gap-1 justify-end">⏱ <span>{rem}</span></div>}
              </div>
            </div>
          );
        })}
      </div>

      {busy && <Box variant="warn" className="text-[14px] mb-2">🔒 invio anonimo… <span className="text-ink-dim">(può tardare qualche secondo)</span></Box>}
      {err && <Box variant="danger" className="text-[14px] mb-2"><span className="text-red-500">✕</span> {err}</Box>}

      <Footer>
        {/* Selettore durata di autodistruzione (come nelle chat normali). */}
        <div className="flex items-center gap-2 mb-1.5 text-[12px]">
          <button type="button" onClick={() => setShowTtl((v) => !v)} className="text-ink-dim hover:text-amber flex items-center gap-1">
            ⏱ <span>sparisce dopo la lettura: <b className="text-amber">{ttlLabel}</b></span>
          </button>
        </div>
        {showTtl && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {TTL_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => { setTtl(o.value); setShowTtl(false); }}
                className={`text-[12px] px-2 py-1 rounded border ${ttl === o.value ? 'border-amber text-amber bg-amber/10' : 'border-ink-faint text-ink-dim'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doSend(); }}
            disabled={busy}
            placeholder="Messaggio anonimo…"
            maxLength={500}
            className="flex-1 min-w-0 bg-black border border-ink-dim text-ink font-mono text-[15px] px-3 py-2.5 rounded-xl focus:border-amber focus:outline-none"
          />
          <button
            onClick={doSend}
            disabled={busy || !text.trim()}
            className="shrink-0 border border-amber rounded-xl bg-amber/10 text-amber px-4 py-2.5 hover:bg-amber/20 transition-colors disabled:opacity-40"
          >
            {busy ? '…' : '▶'}
          </button>
        </div>
        <p className="text-[11px] text-ink-dim mt-1.5">un controllo anti-spam può far tardare l'invio di un secondo.</p>
      </Footer>
    </Frame>
  );
}
